import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FILTERS } from '@core/models/ga4/filters.enum';
import { AnalyticsService } from '@core/services/analytics.service';
import { Ga4Service } from '@core/services/ga4.service';
import { select, Store } from '@ngrx/store';
import { IResults } from '@results/store/results.state';
import { UrlService } from '@services/url.service';
import { selectContextCore } from '@store/context/context.selector';
import { selectRouterQueryParams } from '@store/router/router.selector';
import { getNested } from 'globals';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IHotelsAppState } from './../../store/index';
import { toggleFilter } from '@results/store/results.actions';

@Component({
  selector: 'am-filter-list',
  styleUrls: ['./filters-list.component.scss'],
  templateUrl: './filters-list.component.html',
})
export class FiltersListComponent implements OnInit, OnChanges, OnDestroy {
  get appliedFilters() {
    return this._appliedFilters || [];
  }

  @Input()
  set appliedFilters(value) {
    this._appliedFilters = value;
  }
  constructor(
    private store$: Store<IResults>,
    private analyticsService: AnalyticsService,
    private storeContext$: Store<IHotelsAppState>,
    private ga4Service: Ga4Service,
    private urlService: UrlService,
  ) {
    this.storeContext$
      .pipe(select(selectContextCore), takeUntil(this.unSubscribe$))
      .subscribe((context) => {
        if (this.brands.includes(context.brand)) {
          this.brand = context.brand;
        } else {
          this.brand = 'almundo';
        }
      });
  }

  @Input()
  public filtersList: any;
  public _appliedFilters: any;

  @Input() public section = 'PLP';

  @Input()
  public allFilterList: any;

  @Input()
  public isMobile: boolean;
  @Output()
  public closeFilters: EventEmitter<void> = new EventEmitter<void>();
  @Input() public isArgentina;
  @Input() public isLanding = false;
  private unSubscribe$ = new Subject<void>();
  public filterNameValue: string;
  public params = {};
  public priceRange: { min: number | null; max: number | null };
  public currencyCode: string;
  public queryParams: any;
  public appliedFiltersBackUp: any[];
  public showAppliedFilters: boolean;
  public hasFiltersChanged = false;
  public stickyFilter: HTMLElement;
  public showStickyFilters = false;
  public brands: Array<string> = ['avantrip'];
  public brand: string;
  public filterOrderApply = [
    'FLEXIBLE',
    'PRICE_RANGE',
    'RECOMMENDED',
    'ACCOMMODATION_TYPE',
    'MEAL',
    'CURRENCY',
    'PAYMENT_METHOD',
    'STAR',
    'AMENITY',
    'NAME',
    'DISCOUNTS',
    'RATING',
    'OUTSTANDING',
    'NEARBY',
  ];
  public isExpert = false;
  public currencyFilter = {
    type: 'CURRENCY',
    values: [
      {
        code: 'ARS',
        name: '$ Pesos',
      },
      {
        code: 'USD',
        name: 'u$s Dólares',
      },
    ],
    title: 'CURRENCY',
  };
  public isCurrencyDisabled = false;
  private isFocus = false;

  @HostListener('document:keydown.enter', ['$event']) onKeydownHandler(event: KeyboardEvent) {
    if (this.isFocus && this.filterNameValue.length > 0) {
      event.preventDefault();

      this.changeFilter(null, 'NAME', null);
    }
  }

  public ngOnInit() {
    this.orderFilters();
    this.store$
      .pipe(select(selectRouterQueryParams), takeUntil(this.unSubscribe$))
      .subscribe((data) => {
        this.queryParams = data;
        if (data.currencyCode) {
          this.currencyCode = data.currencyCode.toUpperCase();
        } else {
          this.currencyCode = 'ARS';
        }
        this.setFilterRange();
        this.setFilterName();
      });

    fromEvent(window, 'scroll')
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe(() => {
        this.stickyFiltersVisibility();
      });
    this.checkFilters();
    this.isExpert = this.urlService.isExperts();
  }

  public ngOnChanges() {
    this.setAppliedFilters();
  }

  private checkFilters() {
    if (!this.allFilterList || this.appliedFilters.length >= this.allFilterList?.length) {
      return;
    }
    const filtersApplied = [];
    this.appliedFilters.forEach((filterApplied) => {
      const filterList = this.allFilterList.find((filter) => filter.type === filterApplied.type);
      const newFilterApplied = { ...filterApplied };
      newFilterApplied.values = [];
      if (filterApplied.type !== 'FLEXIBLE') {
        filterApplied.values.forEach((valueApplied) => {
          const value = filterList.values?.find(
            (valueFilter) => !valueApplied.description && valueFilter.code === valueApplied.code,
          );
          if (value) {
            newFilterApplied.values.push(value);
          } else {
            newFilterApplied.values.push(valueApplied);
          }
        });
      } else {
        newFilterApplied.values = filterApplied.values;
      }
      filtersApplied.push(newFilterApplied);
    });

    const hasPaymentMethod = filtersApplied.find((f) => f.type === 'PAYMENT_METHOD');

    if (hasPaymentMethod) {
      if (hasPaymentMethod.values.some((v) => v.code === 'PAY_AT_DESTINATION')) {
        this.isCurrencyDisabled = true;
      }
    }

    if (filtersApplied.length > 0) {
      this.appliedFilters = filtersApplied;
    }
  }

  public setAppliedFilters() {
    this.showAppliedFilters = this.appliedFilters.some((applied) => {
      return applied.type === 'FLEXIBLE' || !!applied.values.length;
    });
  }

  public trackByItems(index) {
    return index;
  }

  private setFilterRange() {
    if (!!this.queryParams.PRICE_RANGE) {
      this.priceRange = { min: 0, max: 0 };
      const range = this.queryParams.PRICE_RANGE.split('-');
      this.priceRange.min = !!range.length ? parseInt(range[0], 10) : null;
      this.priceRange.max = !!range.length ? parseInt(range[1], 10) : null;
    }
  }

  private setFilterName() {
    this.filterNameValue = !!this.queryParams.NAME ? this.queryParams.NAME : null;
  }

  private getAppliedParams(type, item, checked): string {
    if (type === 'flexible') {
      return checked || '';
    }
    if (type === 'NAME') {
      return `${this.filterNameValue}`;
    }
    if (type === 'PRICE_RANGE') {
      return `${item.min}-${item.max}`;
    }
    let res = `${item.code}`;
    if (checked) {
      if (type !== 'DISCOUNTS') {
        if (this.isMobile && !!this.params[type]) {
          res = res + `,${this.params[type]}`;
        }
        if (!!this.queryParams[type]) {
          res = res + `,${this.queryParams[type]}`;
        }
      }
    } else {
      const params =
        this.isMobile && !!this.params[type]
          ? this.params[type].split(',')
          : this.queryParams[type]?.split(',');
      if (params) {
        const filtered = params.filter((fil) => fil !== item.code);
        res = filtered.join(',');
      }
    }
    return res;
  }

  public changeFilter(checked: any, type, item, remove?) {
    let dataGA4 = {};
    if (type !== 'NAME') {
      dataGA4 = {
        type: FILTERS[type],
        label: item.description !== undefined ? item.description : `$${item.min} - $${item.max}`,
        action: item.description !== undefined ? (checked ? 'select' : 'remove') : 'rango',
        section: 'filtros',
      };

      this.ga4Service.user_interaction(dataGA4, this.section);
    } else {
      dataGA4 = {
        search_term: this.filterNameValue,
        section: 'buscador',
      };
      this.ga4Service.search(dataGA4, this.section);
    }

    if (type === 'NAME' && !this.filterNameValue) {
      return;
    }

    if (type === 'FLEXIBLE') {
      type = type.toLowerCase();
    }
    const newParam = {
      [type]:
        (remove && (type === 'PRICE_RANGE' || type === 'NAME' || type === 'DISCOUNTS')) ||
        (type !== 'PRICE_RANGE' &&
          type !== 'DISCOUNTS' &&
          getNested(item, 'code') &&
          item.code.includes('all_'))
          ? ''
          : this.getAppliedParams(type, item, checked),
    };

    this.trackFilter(type, item ? item.description : '', checked ? 'add' : 'remove', false);
    if (!this.isMobile) {
      this.dispatch(newParam);
    } else {
      this.params = {
        ...this.params,
        ...{ [type]: newParam[type] },
      };
      const hasCategoryApplied = this.appliedFilters.find((applied) => applied.type === type);
      if (type !== 'flexible') {
        if (!!hasCategoryApplied) {
          this.appliedFilters = this.appliedFilters.map((filter) => {
            if (filter.type !== type) {
              return filter;
            }
            const hasItemApplied = filter.values.find((value) => value.code === item.code);
            let values = [];
            if (!!hasItemApplied) {
              if (type !== 'NAME') {
                values = [...filter.values.filter((value) => value.code !== item.code)];
              }
            } else {
              if (type !== 'DISCOUNTS' || filter.values?.length === 0) {
                values = [...filter.values, item];
              } else {
                values = [item];
              }
            }
            return {
              type,
              values,
            };
          });
        } else {
          if (type === 'NAME') {
            this.appliedFilters = [
              ...this.appliedFilters,
              { type, values: [{ description: this.filterNameValue }] },
            ];
          } else {
            this.appliedFilters = [...this.appliedFilters, { type, values: [item] }];
          }
        }
        this.setAppliedFilters();
      }
      this.hasFiltersChanged = !!Object.keys(this.params).length;
    }
  }

  public removeAll() {
    this.analyticsService.trackEvent({
      action: 'Clear Filters',
      label: 'Clear',
      section: 'Results',
    });

    /*const dataGA4 = {
      type: 'limpiar',
      label: 'borrar todos',
      action: 'select',
      section: 'filtros',
    };*/

    // this.ga4Service.user_interaction(dataGA4, this.section);
    let newParam = {};
    for (const applied of this.appliedFilters) {
      newParam = {
        ...newParam,
        ...{ [applied.type]: '' },
      };
    }
    this.params = newParam;
    this.appliedFilters = [];
    this.hasFiltersChanged = true;
    this.showAppliedFilters = false;
    if (!this.isMobile) {
      this.dispatch(newParam);
    }
  }

  public dispatch(params?) {
    console.log(params);
    console.log(this.params);
    let newParams = params || this.params;
    newParams = {
      ...newParams,
      offset: '',
    };
    this.store$.dispatch(toggleFilter({ payload: newParams }));
    if (this.isMobile) {
      this.closeFilters.emit();
    }
  }

  public stickyFiltersVisibility() {
    if (!this.filtersList?.length || this.isMobile || this.isLanding) {
      return;
    }

    const lastFilter = this.filtersList[this.filtersList.length - 1];
    const lastFilterElement = document.getElementById(lastFilter?.type);
    const stickyFilter = document.getElementById('RECOMMENDED');

    if (!lastFilterElement || !stickyFilter) {
      return;
    }

    const scrollTop = lastFilterElement.offsetHeight + lastFilterElement.offsetTop - window.scrollY;

    if (scrollTop < 0) {
      stickyFilter.classList.add('sticky');
    } else {
      stickyFilter.classList.remove('sticky');
    }
  }

  public ngOnDestroy() {
    this.unSubscribe$.next();
    this.unSubscribe$.unsubscribe();
  }

  public trackFilter(type, description, action, recommeded) {
    this.analyticsService.trackEvent({
      action: 'Filter',
      label: recommeded
        ? type + '|' + description + '|Recommended|' + action
        : type + '|' + description + '|' + action,
      section: 'Results',
    });
  }

  public orderFilters() {
    const filtersOrder = [];
    let filter;
    if (this.filtersList !== null) {
      if (!this.isArgentina) {
        this.filtersList.push(this.currencyFilter);
      }
      this.filterOrderApply.forEach((filterType) => {
        filter = this.filtersList.find((fil) => fil.type === filterType);
        if (filter !== undefined) {
          filtersOrder.push(filter);
        }
      });
    }
    this.filtersList = filtersOrder;
  }

  onFocus(checkFocus) {
    this.isFocus = checkFocus;
  }

  changeValueSwitch(checked: any, type, item, remove?) {
    item = {
      ...item,
      isApplied: checked.checked,
    };
    this.changeFilter(checked.checked, type, item, remove);
  }

  redirectChangeCurrency(code: 'USD' | 'ARS') {
    if (this.currencyCode === code || this.isCurrencyDisabled) {
      return;
    }
    this.urlService.redirectCurrency(code);
  }
}
